// Security monitoring and logging utilities
interface SecurityEvent {
  type: 'auth_attempt' | 'rate_limit' | 'suspicious_activity' | 'data_access' | 'error';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  metadata: Record<string, any>;
  timestamp: string;
  userAgent?: string;
  ip?: string;
}

class SecurityMonitor {
  private events: SecurityEvent[] = [];
  private readonly maxEvents = 1000; // Keep last 1000 events in memory

  logEvent(event: Omit<SecurityEvent, 'timestamp'>) {
    const securityEvent: SecurityEvent = {
      ...event,
      timestamp: new Date().toISOString(),
      userAgent: navigator?.userAgent?.substring(0, 100),
      ip: 'client-side'
    };

    this.events.unshift(securityEvent);
    
    // Keep only recent events
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(0, this.maxEvents);
    }

    // Log to console with appropriate level
    const logLevel = this.getLogLevel(event.severity);
    console[logLevel](`[SECURITY:${event.severity.toUpperCase()}]`, event.message, event.metadata);

    // In production, you could send to monitoring service
    if (event.severity === 'critical' || event.severity === 'high') {
      this.alertCriticalEvent(securityEvent);
    }
  }

  private getLogLevel(severity: SecurityEvent['severity']): 'log' | 'info' | 'warn' | 'error' {
    switch (severity) {
      case 'low': return 'log';
      case 'medium': return 'info';
      case 'high': return 'warn';
      case 'critical': return 'error';
      default: return 'log';
    }
  }

  private alertCriticalEvent(event: SecurityEvent) {
    // In production, integrate with monitoring service (e.g., Sentry, LogRocket)
    console.error('[SECURITY ALERT]', {
      type: event.type,
      message: event.message,
      timestamp: event.timestamp,
      metadata: event.metadata
    });
  }

  getRecentEvents(limit: number = 50): SecurityEvent[] {
    return this.events.slice(0, limit);
  }

  getEventsByType(type: SecurityEvent['type'], limit: number = 20): SecurityEvent[] {
    return this.events.filter(event => event.type === type).slice(0, limit);
  }

  getEventsBySeverity(severity: SecurityEvent['severity'], limit: number = 20): SecurityEvent[] {
    return this.events.filter(event => event.severity === severity).slice(0, limit);
  }

  // Rate limiting helper for client-side
  checkClientRateLimit(key: string, maxAttempts: number, timeWindow: number): boolean {
    const now = Date.now();
    const storageKey = `security_rate_limit_${key}`;
    
    try {
      const stored = localStorage.getItem(storageKey);
      const attempts: number[] = stored ? JSON.parse(stored) : [];
      
      // Remove expired attempts
      const validAttempts = attempts.filter(timestamp => now - timestamp < timeWindow);
      
      if (validAttempts.length >= maxAttempts) {
        this.logEvent({
          type: 'rate_limit',
          severity: 'medium',
          message: `Rate limit exceeded for ${key}`,
          metadata: { key, attempts: validAttempts.length, maxAttempts, timeWindow }
        });
        return false;
      }
      
      // Add current attempt
      validAttempts.push(now);
      localStorage.setItem(storageKey, JSON.stringify(validAttempts));
      
      return true;
    } catch (error) {
      this.logEvent({
        type: 'error',
        severity: 'low',
        message: 'Error checking rate limit',
        metadata: { error: error instanceof Error ? error.message : 'Unknown error', key }
      });
      return true; // Allow on error to prevent blocking legitimate users
    }
  }

  // Input validation logger
  validateAndLog(input: string, fieldName: string, maxLength: number = 1000): string {
    if (!input || typeof input !== 'string') {
      this.logEvent({
        type: 'suspicious_activity',
        severity: 'low',
        message: `Invalid input type for ${fieldName}`,
        metadata: { fieldName, inputType: typeof input }
      });
      return '';
    }

    if (input.length > maxLength) {
      this.logEvent({
        type: 'suspicious_activity',
        severity: 'medium',
        message: `Input too long for ${fieldName}`,
        metadata: { fieldName, length: input.length, maxLength }
      });
      return input.substring(0, maxLength);
    }

    // Check for potential XSS patterns
    const xssPatterns = [/<script/i, /javascript:/i, /onload=/i, /onerror=/i, /onclick=/i];
    const hasXssPattern = xssPatterns.some(pattern => pattern.test(input));
    
    if (hasXssPattern) {
      this.logEvent({
        type: 'suspicious_activity',
        severity: 'high',
        message: `Potential XSS attempt in ${fieldName}`,
        metadata: { fieldName, inputLength: input.length }
      });
      // Don't log the actual malicious input for security
    }

    return input.trim();
  }

  // Authentication event logger
  logAuthEvent(event: 'login_attempt' | 'login_success' | 'login_failure' | 'logout', metadata: Record<string, any> = {}) {
    const severity = event === 'login_failure' ? 'medium' : 'low';
    
    this.logEvent({
      type: 'auth_attempt',
      severity,
      message: `Authentication event: ${event}`,
      metadata: { event, ...metadata }
    });
  }

  // Data access logger
  logDataAccess(resource: string, action: string, metadata: Record<string, any> = {}) {
    this.logEvent({
      type: 'data_access',
      severity: 'low',
      message: `Data access: ${action} on ${resource}`,
      metadata: { resource, action, ...metadata }
    });
  }

  // Clear old events (useful for privacy compliance)
  clearOldEvents(olderThanDays: number = 30) {
    const cutoff = Date.now() - (olderThanDays * 24 * 60 * 60 * 1000);
    const initialCount = this.events.length;
    
    this.events = this.events.filter(event => {
      return new Date(event.timestamp).getTime() > cutoff;
    });

    const removedCount = initialCount - this.events.length;
    if (removedCount > 0) {
      this.logEvent({
        type: 'data_access',
        severity: 'low',
        message: `Cleared ${removedCount} old security events`,
        metadata: { removedCount, olderThanDays }
      });
    }
  }
}

// Singleton instance
export const securityMonitor = new SecurityMonitor();

// Utility functions for common security operations
export const logSecurityEvent = (event: Omit<SecurityEvent, 'timestamp'>) => {
  securityMonitor.logEvent(event);
};

export const validateInput = (input: string, fieldName: string, maxLength?: number) => {
  return securityMonitor.validateAndLog(input, fieldName, maxLength);
};

export const checkRateLimit = (key: string, maxAttempts: number, timeWindow: number) => {
  return securityMonitor.checkClientRateLimit(key, maxAttempts, timeWindow);
};

export const logAuth = (event: 'login_attempt' | 'login_success' | 'login_failure' | 'logout', metadata?: Record<string, any>) => {
  securityMonitor.logAuthEvent(event, metadata);
};

export const logDataAccess = (resource: string, action: string, metadata?: Record<string, any>) => {
  securityMonitor.logDataAccess(resource, action, metadata);
};
